import { ReactComponent as PhoneIcon } from 'Assets/icons/call.svg';
import { ReactComponent as HangUpIcon } from 'Assets/icons/hangup_v2.svg';
import { ReactComponent as VideoIcon } from 'Assets/icons/video_v2.svg';
import { SoftphoneShortcutTooltip } from 'Modules/softphone/components/SoftphoneShortcutTooltip';
import { getShortcutAriaLabel } from 'Modules/softphone/constants/keyboardShortcuts';
import { getCallInviteCopy } from 'Modules/softphone/utils/callActionCopy';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import { IconButton } from '~/lib/ui/icon-button';
import { CallBannerActionsProps } from './index.types';

/**
 * CallBannerActions displays the action buttons for the call banner.
 *
 * Provides two actions:
 * - Decline: Rejects the incoming call (destructive variant)
 * - Accept: Accepts the incoming call (success variant)
 *
 * @remarks
 * The button labels and tooltips adapt based on whether it's a video or voice call.
 * The actions container has flex-shrink-0 to maintain fixed width.
 */
export const CallBannerActions = ({
  callerLabel,
  isVideo,
  onAccept,
  onDecline,
}: CallBannerActionsProps) => {
  const setImagePreviewOpen = useUIStore((state) => state.setImagePreviewOpen);

  const acceptCopy = getCallInviteCopy('accept', { isVideo });
  const declineCopy = getCallInviteCopy('decline', { isVideo });

  const handleAccept = () => {
    // Close image preview before accepting the call
    setImagePreviewOpen(false);
    onAccept();
  };

  return (
    <div className="flex items-center gap-[12px] flex-shrink-0">
      <SoftphoneShortcutTooltip action="decline" label={declineCopy.tooltip}>
        <div>
          <IconButton
            icon={HangUpIcon}
            label={declineCopy.caption}
            onClick={onDecline}
            variant="destructive"
            iconClassName="brightness-0 invert"
            ariaLabel={getShortcutAriaLabel(
              `${declineCopy.tooltip} from ${callerLabel}`,
              'decline'
            )}
          />
        </div>
      </SoftphoneShortcutTooltip>

      <SoftphoneShortcutTooltip action="accept" label={acceptCopy.tooltip}>
        <div>
          <IconButton
            icon={isVideo ? VideoIcon : PhoneIcon}
            label={acceptCopy.caption}
            onClick={handleAccept}
            variant="success"
            iconClassName="brightness-0 invert"
            // The accessible name is the tooltip plus who's calling.
            ariaLabel={getShortcutAriaLabel(
              `${acceptCopy.tooltip} from ${callerLabel}`,
              'accept'
            )}
          />
        </div>
      </SoftphoneShortcutTooltip>
    </div>
  );
};
